import React, { useEffect, useState } from 'react';
import {
    Typography,
    Grid,
    Divider,
    Button,
    Box,
    Skeleton,
    Avatar,
    Paper,
    Alert,
    Snackbar
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewPlanDetailsModal from './ViewPlanDetailsModal';
import ChangePlanModal from './ChangePlanModal';
import ComparePlansModal from './ComparePlansModal';
import { updateHouse } from '../../../api_calls/api_calls_houses';
import { getHouseEntity } from '../../../api_calls/api_calls_houses';
import { getLatestInvoiceData, getCurrentMonthCost } from '../../../api_calls/api_calls_invoices';

const PROVIDER_LOGO_PATH = '/images/providers/';

const HouseMonitoringBillingInfo = ({ id }) => {
    const houseId = id;
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [house, setHouse] = useState(null);
    const [invoice, setInvoice] = useState(null);
    const [cost, setCost] = useState(null);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
    const [comparePlansModalOpen, setComparePlansModalOpen] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);
        Promise.all([
            getHouseEntity(houseId),
        ])
            .then(async ([houseData]) => {
                if (!mounted) return;
                setHouse(houseData);
                const [invoiceData, costData] = await Promise.all([
                    getLatestInvoiceData(houseData.invoiceprovidername, houseData.invoicename),
                    getCurrentMonthCost(houseId)
                ]);
                if (!mounted) return;
                setInvoice(invoiceData);
                setCost(costData);
                setLoading(false);
            })
            .catch((e) => {
                if (!mounted) return;
                setError(e.message);
                setLoading(false);
            });
        return () => { mounted = false; };
    }, [houseId]);

    return (
        <Paper
            elevation={0}
            sx={{
                padding: '20px',
                borderRadius: '25px',
                backgroundColor: theme.palette.common.white,
                width: '100%',
                minHeight: 700,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
            }}
        >
            <Typography
                variant="h5"
                align="center"
                sx={{ fontWeight: 'bold' }}
                className="fancyText"
                gutterBottom
            >
                Billing Information
            </Typography>
            {loading ? (
                <Skeleton variant="rectangular" height={160} />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <>
                    <Grid container alignItems="center" spacing={1} sx={{ mt: 2 }}>
                        <Grid item>
                            <Avatar
                                src={PROVIDER_LOGO_PATH + (house.invoiceprovidername || 'DEI') + '.png'}
                                alt={house.invoiceprovidername}
                                sx={{ width: 48, height: 48, bgcolor: theme.palette.grey[100] }}
                            />
                        </Grid>
                        <Grid item xs>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {house.invoiceprovidername} {house.invoicename}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Typography variant="caption" color="success.main" sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 600 }}>
                                    <span style={{ color: theme.palette.success.main, fontSize: 18, marginRight: 4 }}>●</span>
                                    Active Plan
                                </Typography>
                            </Typography>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                        <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <BoltIcon color="primary" sx={{ fontSize: 28 }} />
                            <Typography variant="caption" display="block" color="text.secondary">Current Rate</Typography>
                            <Typography variant="subtitle1" fontWeight={600}>
                                €{invoice.day_price_high?.toFixed(4) || '-'}<span style={{ fontSize: 12 }}>/kWh</span>
                            </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <SwapHorizIcon color="primary" sx={{ fontSize: 28 }} />
                            <Typography variant="caption" display="block" color="text.secondary">Average Monthly</Typography>
                            <Typography variant="subtitle1" fontWeight={600}>
                                €{cost?.average?.toFixed(2) || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <EventNoteIcon color="primary" sx={{ fontSize: 28 }} />
                            <Typography variant="caption" display="block" color="text.secondary">Month to Date</Typography>
                            <Typography variant="subtitle1" fontWeight={600}>
                                €{cost?.cost?.toFixed(2) || '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ my: 2 }} />
                    <Divider />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<InfoOutlinedIcon sx={{ fontSize: 32 }} />}
                            onClick={() => setModalOpen(true)}
                            sx={{
                                borderRadius: 8,
                                fontWeight: 700,
                                mb: 1,
                                fontSize: 18,
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                color: 'white',
                                boxShadow: 'none',
                                textTransform: 'none',
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                    background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                    boxShadow: 'none',
                                },
                            }}
                            fullWidth
                        >
                            View Plan Details
                        </Button>
                    </Box>
                </>
            )}
            {/* Add a Box with flexGrow: 1 to push the bottom section down */}
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Looking for better rates?
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<SwapHorizIcon sx={{ fontSize: 32 }} />}
                    fullWidth
                    onClick={() => setComparePlansModalOpen(true)}
                    sx={{
                        py: 1.5,
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 18,
                        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        color: 'white',
                        boxShadow: 'none',
                        textTransform: 'none',
                        '&:hover': {
                            background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                            boxShadow: 'none',
                        },
                    }}
                >
                    Compare Plans
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<SettingsIcon sx={{ fontSize: 32 }} />}
                    fullWidth
                    onClick={() => setChangePlanModalOpen(true)}
                    sx={{
                        py: 1.5,
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 18,
                        mt: 2,
                        textTransform: 'none',
                    }}
                >
                    Edit Electricity Plan
                </Button>
            </Box>
            <ViewPlanDetailsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                houseData={house}
            />
            {updateError && (
                <Snackbar
                    open={!!updateError}
                    autoHideDuration={6000}
                    onClose={() => setUpdateError(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setUpdateError(null)} severity="error" sx={{ width: '100%' }}>
                        {updateError}
                    </Alert>
                </Snackbar>
            )}
            <ChangePlanModal
                open={changePlanModalOpen}
                onClose={() => {
                    setChangePlanModalOpen(false);
                    setUpdateError(null);
                }}
                houseId={houseId}
                currentPlanProviderName={house?.invoiceprovidername}
                currentPlanName={house?.invoicename}
                onPlanSelect={(plan) => {
                    setUpdateError(null);

                    const updateData = {
                        invoiceprovidername: plan.provider_name,
                        invoicename: plan.name
                    };

                    updateHouse(houseId, updateData)
                        .then(() => {
                            // Refresh house data after successful update
                            getHouseEntity(houseId)
                                .then(async (houseData) => {
                                    setHouse(houseData);
                                    // Refresh invoice and cost data
                                    const [invoiceData, costData] = await Promise.all([
                                        getLatestInvoiceData(houseData.invoiceprovidername, houseData.invoicename),
                                        getCurrentMonthCost(houseId)
                                    ]);
                                    setInvoice(invoiceData);
                                    setCost(costData);
                                    setChangePlanModalOpen(false);
                                })
                                .catch((e) => {
                                    setUpdateError(e.message || 'Failed to refresh house data');
                                });
                        })
                        .catch((error) => {
                            setUpdateError(error.message || 'Failed to update plan');
                        });
                }}
            />
            <ComparePlansModal
                open={comparePlansModalOpen}
                onClose={() => {
                    setComparePlansModalOpen(false);
                    setUpdateError(null);
                }}
                houseId={houseId}
                currentPlanProviderName={house?.invoiceprovidername}
                currentPlanName={house?.invoicename}
                onPlanUpdate={() => {
                    // Refresh house data after successful plan update
                    getHouseEntity(houseId)
                        .then(async (houseData) => {
                            setHouse(houseData);
                            // Refresh invoice and cost data
                            const [invoiceData, costData] = await Promise.all([
                                getLatestInvoiceData(houseData.invoiceprovidername, houseData.invoicename),
                                getCurrentMonthCost(houseId)
                            ]);
                            setInvoice(invoiceData);
                            setCost(costData);
                        })
                        .catch((e) => {
                            setUpdateError(e.message || 'Failed to refresh house data');
                        });
                }}
            />
        </Paper>
    );
};

export default HouseMonitoringBillingInfo;